import { tokenInterceptor, NIKO_API_URL, NotificationEffects, versionedAssetUrl } from '@niko-crm/ui';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideAdminStore } from '@niko-crm/store';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

import {
  ApplicationConfig,
  Injectable,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { Amplify } from 'aws-amplify';
import { provideTransloco, TranslocoLoader } from '@jsverse/transloco';
import { appRoutes } from './app.routes';

import { environment } from '../environments/environment';

const NIKO_LANG_KEY = 'niko_lang';

@Injectable({ providedIn: 'root' })
class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  getTranslation(lang: string) {
    // See the niko-crm twin: `assets/` is never fingerprinted by
    // `outputHashing`, so the URL needs its own cache-busting token.
    return this.http.get<Record<string, unknown>>(
      versionedAssetUrl(`/assets/i18n/${lang}.json`),
    );
  }
}

// Aura paints muted text/icons with surface.400/500, which our palette makes too pale.
const MUTED = 'color-mix(in srgb, {surface.500}, black 30%)';
const MUTED_ICON = 'color-mix(in srgb, {surface.400}, black 30%)';

const NikoTheme = definePreset(Aura, {
  semantic: {
    primary: {
      50:  '#e8eef7',
      100: '#c6d5ea',
      200: '#a0bbdc',
      300: '#79a0ce',
      400: '#5a8bc4',
      500: '#3c76ba',
      600: '#2d619f',
      700: '#1e4d84',
      800: '#1e3a5f',
      900: '#0f2340',
      950: '#071525',
    },
    colorScheme: {
      light: {
        surface: {
          0:   '#ffffff',
          50:  '#f6f8fa',
          100: '#eef1f5',
          200: '#e4e9ef',
          300: '#d8dfe8',
          400: '#cdd5e0',
          500: '#bec9d6',
        },
        primary: {
          color:         '#1e3a5f',
          contrastColor: '#ffffff',
          hoverColor:    '#1e4d84',
          activeColor:   '#2d619f',
        },
        text: { mutedColor: MUTED },
        formField: {
          disabledColor: MUTED,
          placeholderColor: MUTED,
          floatLabelColor: MUTED,
          floatLabelActiveColor: MUTED,
          iconColor: MUTED_ICON,
        },
        list: {
          option: { icon: { color: MUTED_ICON, focusColor: MUTED } },
        },
        navigation: {
          item: { icon: { color: MUTED_ICON, focusColor: MUTED, activeColor: MUTED } },
          submenuIcon: { color: MUTED_ICON, focusColor: MUTED, activeColor: MUTED },
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          outlined: { secondary: { color: MUTED } },
          text: { secondary: { color: MUTED } },
        },
      },
    },
    togglebutton: {
      colorScheme: {
        light: { root: { color: MUTED }, icon: { color: MUTED } },
      },
    },
    inputnumber: {
      colorScheme: {
        light: { button: { color: MUTED_ICON, hoverColor: MUTED } },
      },
    },
    message: {
      colorScheme: {
        light: {
          secondary: { outlined: { color: MUTED }, simple: { color: MUTED } },
        },
      },
    },
  },
});

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: environment.cognitoUserPoolId,
      userPoolClientId: environment.cognitoClientId,
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes),
    provideHttpClient(withInterceptors([tokenInterceptor])),
    provideAnimationsAsync(),
    providePrimeNG({ theme: { preset: NikoTheme, options: { darkModeSelector: '.niko-dark', cssLayer: false } } }),
    provideAdminStore(),
    provideEffects([NotificationEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
    { provide: NIKO_API_URL, useValue: environment.apiUrl },
    MessageService,
    provideTransloco({
      config: {
        availableLangs: ['es', 'en'],
        defaultLang: (localStorage.getItem(NIKO_LANG_KEY) as 'es' | 'en') ?? 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
  ],
};
